/**
 * <s-top-bar>: rotates the announcement messages (blocks/top-bar). One message is [data-active]; the others are
 * aria-hidden. Auto-advances every data-interval ms when [data-auto], pausing while hovered, focused or when the
 * tab is hidden. Previous/next buttons step through. External links open in a new tab.
 */
class STopBar extends HTMLElement {
  #index = 0;
  #timer = 0;
  #paused = false;

  connectedCallback() {
    this.messages = [...this.querySelectorAll('.topbar-msg')];
    this.querySelectorAll('a[href^="http"]').forEach((link) => {
      if (link.host !== window.location.host) {
        link.target = '_blank';
        link.rel = 'noopener';
      }
    });
    this.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener('click', () => this.#go(this.#index + Number(button.dataset.step)));
    });
    const pause = () => { this.#paused = true; this.#stop(); };
    const resume = () => { this.#paused = false; this.#start(); };
    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget)) resume(); });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.#stop() : this.#start()));
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
  }

  #start() {
    this.#stop();
    if (!this.hasAttribute('data-auto') || this.#paused || this.messages.length < 2) return;
    const interval = Number(this.dataset.interval) || 5000;
    this.#timer = window.setInterval(() => this.#go(this.#index + 1), interval);
  }

  #stop() {
    window.clearInterval(this.#timer);
  }

  #go(next) {
    const count = this.messages.length;
    if (count < 2) return;
    const target = ((next % count) + count) % count;
    this.style.setProperty('--topbar-dir', next < this.#index ? '-1' : '1');
    this.messages[this.#index].removeAttribute('data-active');
    this.messages[this.#index].setAttribute('aria-hidden', 'true');
    this.messages[target].setAttribute('data-active', '');
    this.messages[target].removeAttribute('aria-hidden');
    this.#index = target;
  }
}

if (!customElements.get('s-top-bar')) customElements.define('s-top-bar', STopBar);
